<script>
  import { onMount, onDestroy } from 'svelte';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';
  import gsap from 'gsap';

  export let width = '100%';
  export let height = '100vh';
  export let scrollMapped = true;
  export let scrollMultiplier = 0.1; // Multiplier to slow down animation
  export let scrubDelay = 0.2; // Delay for smoother scrubbing
  export let sequenceBasePath = './ev_sequence'; // Base path for sequence images
  export let totalFrames = 513; // Total number of frames (0-513)
  export let preloadRange = 20; // Number of frames to preload ahead/behind (increased for smoother playback)

  let imageElement;
  let containerElement;
  let currentFrame = 200;
  let scrollTriggerSetup = false;
  let setupAttempted = false;
  let currentScrollProgress = 0;
  let preloadedImages = new Map(); // Cache for preloaded images
  let rafId = null;
  let pendingFrame = null;
  let lastPreloadedFrame = -1;

  // Format frame number with leading zeros (e.g., 0 -> "000", 42 -> "042", 513 -> "513")
  function formatFrameNumber(frame) {
    return frame.toString().padStart(3, '0');
  }

  // Get image source path for a frame
  function getFramePath(frame) {
    return `${sequenceBasePath}${formatFrameNumber(frame)}.jpg`;
  }

  // Get image source path for current frame
  $: imageSrc = getFramePath(currentFrame);

  // Preload a single image
  function preloadImage(frame) {
    if (frame < 0 || frame > totalFrames || preloadedImages.has(frame)) {
      return;
    }

    const img = new Image();
    const path = getFramePath(frame);
    
    img.onload = () => {
      preloadedImages.set(frame, img);
    };
    
    img.onerror = () => {
      console.warn(`Failed to load frame ${frame}`);
    };
    
    img.src = path;
  }

  // Preload a range of frames around the current frame
  function preloadFrames(centerFrame) {
    const startFrame = Math.max(0, centerFrame - preloadRange);
    const endFrame = Math.min(totalFrames, centerFrame + preloadRange);
    
    // Only preload if we've moved significantly (reduces unnecessary preloading)
    if (Math.abs(centerFrame - lastPreloadedFrame) < preloadRange / 3) {
      return;
    }
    
    lastPreloadedFrame = centerFrame;
    
    // Preload frames in priority order: immediate neighbors first, then further away
    const framesToPreload = [];
    for (let i = startFrame; i <= endFrame; i++) {
      if (!preloadedImages.has(i)) {
        const distance = Math.abs(i - centerFrame);
        framesToPreload.push({ frame: i, distance });
      }
    }
    
    // Sort by distance (closer frames first)
    framesToPreload.sort((a, b) => a.distance - b.distance);
    
    // Load immediate frames synchronously (0-2 distance), others with small delay
    framesToPreload.forEach(({ frame, distance }) => {
      if (distance <= 2) {
        preloadImage(frame); // Immediate frames
      } else {
        setTimeout(() => preloadImage(frame), (distance - 2) * 5); // Others with priority
      }
    });
  }

  // Update frame number based on scroll progress with throttling
  function updateFrame(progress) {
    const clampedProgress = Math.max(0, Math.min(1, progress));
    const targetFrame = Math.round(clampedProgress * totalFrames);
    
    pendingFrame = targetFrame;
    
    // Use requestAnimationFrame for smooth updates
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        if (pendingFrame !== null) {
          currentFrame = pendingFrame;
          preloadFrames(currentFrame);
          pendingFrame = null;
        }
        rafId = null;
      });
    }
  }

  // Preload initial frames
  function preloadInitialFrames() {
    // Preload first batch of frames more aggressively
    const initialLoadCount = Math.min(preloadRange * 3, totalFrames);
    for (let i = 0; i <= initialLoadCount; i++) {
      // Load first frames immediately, stagger the rest slightly
      if (i <= preloadRange) {
        preloadImage(i);
      } else {
        setTimeout(() => preloadImage(i), (i - preloadRange) * 10);
      }
    }
  }

  onMount(() => {
    // Initialize frame to 0
    currentFrame = 0;
    
    // Start preloading initial frames
    preloadInitialFrames();
    
    if (scrollMapped) {
      // Wait for panels to be ready before setting up scroll mapping
      const initializeScrollMapping = () => {
        // Prevent duplicate setup attempts
        if (setupAttempted && scrollTriggerSetup) {
          return;
        }
        
        const panelsReady = document.getElementById('panel1') && document.getElementById('panel7');
        
        if (panelsReady) {
          if (!scrollTriggerSetup) {
            setupScrollMapping();
            scrollTriggerSetup = true;
          }
        } else {
          // Check more frequently, but don't wait too long
          setupAttempted = true;
          setTimeout(initializeScrollMapping, 50);
        }
      };
      
      // Start checking immediately
      initializeScrollMapping();
      
      // Fallback timeout
      setTimeout(() => {
        if (!scrollTriggerSetup) {
          initializeScrollMapping();
        }
      }, 500);
    }
  });

  onDestroy(() => {
    // Cancel any pending RAF calls
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    
    // Clean up ScrollTrigger instances
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars && trigger.vars.id === 'sequence-scroll-trigger') {
        trigger.kill();
      }
    });
    
    // Clear preloaded images cache
    preloadedImages.clear();
    
    // Reset flags
    scrollTriggerSetup = false;
    setupAttempted = false;
  });

  function setupScrollMapping() {
    if (scrollTriggerSetup) return;

    // Kill any existing ScrollTrigger with this specific ID to prevent conflicts
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars && trigger.vars.id === 'sequence-scroll-trigger') {
        trigger.kill();
      }
    });

    // Ensure frame starts at 0
    currentFrame = 0;
    
    // Mark as setup to prevent duplicates
    scrollTriggerSetup = true;

    // Create a ScrollTrigger that maps scroll position to frame number across multiple panels
    const trigger = ScrollTrigger.create({
      id: 'sequence-scroll-trigger',
      trigger: document.getElementById('panel1'), // Start from first panel
      start: 'top top', // Start when first panel hits top of viewport
      end: () => {
        // Cache the calculation to avoid repeated DOM queries
        const lastPanel = document.getElementById('panel7');
        if (!lastPanel) return '+=1000px';
        
        // Cache height calculation
        const baseHeight = lastPanel.offsetHeight || 100;
        // Extend the scroll range inversely to the scrollMultiplier
        // If scrollMultiplier is 0.1, we need 10x more scroll distance
        const extendedHeight = baseHeight / scrollMultiplier;
        return `+=${extendedHeight}px`;
      },
      invalidateOnRefresh: false, // Prevent recalculation on refresh for better performance
      scrub: scrubDelay, // Smooth scrubbing
      onUpdate: (self) => {
        currentScrollProgress = Math.max(0, Math.min(1, self.progress));
        updateFrame(currentScrollProgress);
        // Preload frames around current position
        const frame = Math.round(currentScrollProgress * totalFrames);
        preloadFrames(frame);
      },
      onRefresh: () => {
        // Ensure frame is at correct position when ScrollTrigger refreshes
        currentFrame = 0;
      },
      onEnter: () => {
        // When entering the trigger area, ensure frame starts at 0
        currentFrame = 0;
      },
      onLeave: () => {
        // When leaving the trigger area, keep frame at current position
        // Don't reset when leaving
      },
      onEnterBack: () => {
        // When scrolling back into the trigger area, reset frame
        currentFrame = 0;
      },
      onStart: () => {
        // Force frame to start at 0 when trigger starts
        currentFrame = 0;
      }
    });
    
    console.log('ScrollTrigger created for sequence:', trigger);
  }

  function handleImageLoaded() {
    // Image is loaded and ready
    // No specific action needed for images
  }
</script>

<div 
  bind:this={containerElement}
  class="video-animation-container"
  style="width: {width}; height: {height};"
>
  <!-- Image sequence that scrubs based on scroll progress -->
  <img
    bind:this={imageElement}
    class="sequence-image"
    src={imageSrc}
    alt="Animation sequence"
    on:load={handleImageLoaded}
  />

</div>

<style>
  .video-animation-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #000;
    overflow: hidden;
    cursor: pointer;
    z-index: 2; /* Match video z-index */
    transform: translateZ(0); /* Force GPU acceleration */
    will-change: transform;
  }

  .sequence-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    will-change: contents;
    transform: translateZ(0); /* Force GPU acceleration */
    backface-visibility: hidden;
    image-rendering: auto; /* Let browser optimize for performance */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Responsive video sizing */
  @media (max-width: 1200px) {
    .video-animation-container {
      height: 100vh;
    }
  }

  @media (max-width: 768px) {
    .video-animation-container {
      height: 100vh;
    }
  }

  @media (max-width: 480px) {
    .video-animation-container {
      height: 100vh;
    }
  }

  /* Portrait orientation adjustments */
  @media (orientation: portrait) and (max-width: 768px) {
    .sequence-image {
      object-fit: cover;
      object-position: center center;
    }
  }


  /* Responsive design */
  @media (max-width: 768px) {
    /* Responsive styles can be added here if needed */
  }
</style>